import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CountryRestrictionService {
  private readonly _defaultAllowedCountries: string[] = (() => {
    const configured = (environment.allowedCountries ?? ['NO'])
      .map(code => (code || '').toUpperCase())
      .filter(code => !!code);

    return configured.length ? configured : ['NO'];
  })();
  private _allowedCountries: string[] = [...this._defaultAllowedCountries];
  private _translateService: TranslateService = inject(TranslateService);

  setAllowedCountries(countries: string[] | undefined): void {
    if (Array.isArray(countries) && countries.length) {
      this._allowedCountries = countries
        .map(code => (code || '').toUpperCase())
        .filter(code => !!code);
    } else {
      this._allowedCountries = [...this._defaultAllowedCountries];
    }
  }

  getAllowedCountries(): string[] {
    return [...this._allowedCountries];
  }

  isCountryAllowed(countryCode: string | undefined | null): boolean {
    if (!countryCode) {
      return false;
    }

    return this._allowedCountries.indexOf(countryCode.toUpperCase()) !== -1;
  }

  getCountryDisplayName(countryCode: string | undefined | null): string {
    if (!countryCode) {
      return '';
    }

    const code = countryCode.toUpperCase();
    const language = this._translateService?.currentLang || 'en';

    if (typeof Intl !== 'undefined' && 'DisplayNames' in Intl) {
      try {
        const DisplayNamesCtor = (Intl as any).DisplayNames;
        if (typeof DisplayNamesCtor === 'function') {
          const displayNames = new DisplayNamesCtor([language], { type: 'region' });
          return displayNames.of(code) || code;
        }
      } catch (error) {
        console.warn('Failed to resolve country display name', error);
        return code;
      }
    }

    return code;
  }

  getAllowedCountriesDisplay(): string {
    const displayNames = this._allowedCountries
      .map(code => this.getCountryDisplayName(code) || code.toUpperCase())
      .filter(Boolean);

    return displayNames.join(', ');
  }
}

